import Modal from '@Components/Utilities/Popup/Modal'
import { FlexLayout } from '@eduact/ed-system'
import moment from 'moment'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Badge } from '@Components/AbwaabPlus'
import TrustedDevicesApi, { useLogoutDeviceMutation } from '../Settings.api'

type Props = {
	device: Device
	setDevices: (devices: Device[]) => void
	isCurrentDevice?: boolean
}

/**
 * One device row per the design: emoji tile · "agent — type" (+ هذا الجهاز
 * badge for the current device) · last login + count · خروج pill (with the
 * existing confirm modal and logout mutation).
 */
const DeviceCard: FC<Props> = ({ device, setDevices, isCurrentDevice }) => {
	const [t] = useTranslation('settings')
	const [openModel, setOpenModel] = useState<boolean>(false)
	const [deleteDevice] = useLogoutDeviceMutation()

	const getAllDevices = async () => {
		const {
			data: { data },
		} = await TrustedDevicesApi.getAllDevices()
		setDevices(data)
	}

	const logoutSingleDevice = async () => {
		await deleteDevice({
			// Server-minted uuid. `device_id` was chosen by the browser, so it was
			// never a safe thing to look a row up by.
			uuid: device.uuid,
		}).then(() => {
			getAllDevices()
		})
	}

	return (
		<>
			<ModelStyle modalOpen={openModel}>
				<Modal.Content
					minWidth={'fit-content'}
					minHeight={'fit-content'}
					height="13.994rem"
					onClose={() => {
						setOpenModel(false)
					}}
				>
					<ContentWrapper justifyContent="space-between" alignItems="center" flexDirection="column" height="100%">
						<FlexLayout
							p={{ lg: '3.688rem', sm: '2.688rem' }}
							pb={{ lg: '0', sm: '0' }}
							flexDirection="column"
							alignItems="flex-start"
							gridGap="0.875rem"
						>
							<Note>{t('note')}</Note>
						</FlexLayout>
						<FlexLayout
							justifyContent="space-between"
							alignItems="center"
							width="100%"
							p={{ lg: '0 2.813rem 1.8rem', sm: '0 1.688rem 1.5rem' }}
						>
							<DialogButton
								data-tone="muted"
								onClick={() => {
									setOpenModel(false)
								}}
							>
								{t('discard')}
							</DialogButton>
							<DialogButton
								data-tone="danger"
								onClick={() => {
									logoutSingleDevice()
								}}
							>
								{t('confirm')}
							</DialogButton>
						</FlexLayout>
					</ContentWrapper>
				</Modal.Content>
			</ModelStyle>

			<Row>
				<span className="tile" aria-hidden>
					{device.type === 'mobile' ? '📱' : '💻'}
				</span>
				<span className="mid">
					<span className="name">
						<span className="agent">
							{device.agent} — {t(`${device.type}`)}
						</span>
						{isCurrentDevice && <Badge tone="success-tint">{t('currentDevice')}</Badge>}
					</span>
					<span className="meta">
						{t('lastLogin')}: <span dir="ltr">{moment(device.last_login).format('YYYY/MM/DD hh:mm A')}</span> · {device.login_count}{' '}
						{t('times', { defaultValue: 'مرات' })}
					</span>
				</span>
				{!isCurrentDevice && (
					<LogoutPill type="button" onClick={() => setOpenModel(true)}>
						{t('logout_device', { defaultValue: 'خروج' })}
					</LogoutPill>
				)}
			</Row>
		</>
	)
}

const ModelStyle = styled(Modal)`
	display: flex;
	flex-direction: column;
	background-color: #fff;
	color: rgba(0, 0, 0, 0.87);
	border-radius: 4px;
	box-shadow: 0px 11px 15px -7px rgb(0 0 0 / 20%), 0px 24px 38px 3px rgb(0 0 0 / 14%), 0px 9px 46px 8px rgb(0 0 0 / 12%);
	margin: 32px;
	position: relative;
	overflow-y: auto;
`
const ContentWrapper = styled(FlexLayout)`
	font-family: var(--font-sans);
	gap: 1rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		gap: 1.938rem;
	}`}
`
const Note = styled.p`
	font-size: 1rem;
	font-weight: 600;
	color: var(--text-primary);
`
const DialogButton = styled.button`
	height: 38px;
	padding: 0 18px;
	border: none;
	border-radius: var(--r-md);
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
	&[data-tone='muted'] {
		background: var(--surface-sunken-strong);
		color: var(--text-primary);
	}
	&[data-tone='danger'] {
		background: var(--error);
		color: #fff;
	}
`
const Row = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	padding: 14px 16px;
	display: flex;
	align-items: center;
	gap: 12px;
	.tile {
		flex: none;
		width: 44px;
		height: 44px;
		border-radius: 12px;
		background: var(--surface-sunken-strong);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 20px;
	}
	.mid {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
		text-align: start;
	}
	.name {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
	}
	.agent {
		font: var(--fw-bold) 14px var(--font-sans);
		color: var(--text-primary);
	}
	.meta {
		font: var(--fw-regular) 11px var(--font-sans);
		color: var(--text-secondary);
	}
`
const LogoutPill = styled.button`
	flex: none;
	border: 1px solid var(--error);
	background: var(--error-10);
	color: var(--error-dark);
	border-radius: var(--r-full);
	padding: 6px 14px;
	font: var(--fw-bold) 12px var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
`
export default DeviceCard
